import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import imageCompression from "browser-image-compression";
import {
  Avatar,
  Button,
  Input,
  Textarea,
  Alert,
} from "@material-tailwind/react";
import { XMarkIcon } from "@heroicons/react/20/solid";
import {
  getBotDetails,
  handleUploadLogo,
  updateBot,
} from "../../store/bot/actions";
import BotStarters from "./BotStarters";
import BotTrainWebsites from "./BotTrainWebsites";
import BotAllDocs from "./BotAllDocs";
import OpenAI from "openai";
import { CircularProgress } from "@mui/material";
import CreatableSelect from "react-select/creatable";
import Select from "react-select";
import {
  entheogenicOptions,
  focusAreaOptions,
  practitionerTitleoptions,
  toneOptions,
} from "../../utils/createBotData";
import { toast } from "react-toastify";
import { setSessionExpired } from "../../context";
import http from "../../services/http-common";
import { useMutation } from "react-query";

const api =
  "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";

const BotUpdates = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { botDetails } = useSelector((state) => state.bot);
  const [loading, setLoading] = useState(false);
  const [fileName, setFileName] = useState("Change Logo");
  const [isUploading, setIsUploading] = useState(false);
  const [previewAvatar, setPreviewAvatar] = useState(null);
  const [visible, setVisible] = useState(true);

  const onDismiss = () => setVisible(false);

  useEffect(() => {
    dispatch(getBotDetails());
  }, [dispatch]);

  const selectLoginState = (state) => state.bot;
  const BotProperties = createSelector(selectLoginState, (bot) => ({
    error: bot.error,
    success: bot.success,
  }));

  const { error, success } = useSelector(BotProperties);

  useEffect(() => {
    botDetails?.iconUrl &&
      setPreviewAvatar(botDetails?.iconUrl ? botDetails?.iconUrl : null);
  }, [botDetails]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      id: botDetails?.id || "",
      botName: botDetails?.botName,
      coachName: botDetails?.coachName,
      practitionerTitle: botDetails?.practitionerTitle || "",
      tone: botDetails?.tone || "",
      focusArea: botDetails?.focusArea,
      entheogenicSpecialty: botDetails?.entheogenicSpecialty,
      footerNote: botDetails?.footerNote || "",
      iconUrl: botDetails?.iconUrl,
      greeting: botDetails?.greeting,
      kcId: user?.id || "",
      submit: null,
    },
    onSubmit: async (values, helpers) => {
      dispatch(updateBot(values, helpers));
      setFileName("Upload Logo");
    },
  });

  const onUploadCallBack = ({ status, returnData }) => {
    if (status === "success") {
      setIsUploading(false);
      validation.setFieldTouched("iconUrl", true);
      validation.setFieldValue("iconUrl", returnData.data.imageUrl);
      setFileName("Upload Successfully");
    } else {
      setIsUploading(false);
      console.log(returnData.message);
    }
  };

  const imageSelect = async (e) => {
    setIsUploading(true);
    const imageFile = e.currentTarget.files[0];
    const formData = new FormData();

    const reader = new FileReader();
    reader.onload = function (event) {
      const img = new Image();
      img.onload = function () {
        console.log(` ${img.width}x${img.height} Pixels`);
      };
      img.src = event.target.result;
    };

    const options = {
      maxSizeMB: 1,
      maxWidthOrHeight: 1000,
      useWebWorker: true,
      fileType: ".png",
    };
    try {
      const compressedFile = await imageCompression(imageFile, options);
      formData.append("file", compressedFile);
      dispatch(handleUploadLogo(formData, onUploadCallBack));
      compressedFile && setPreviewAvatar(URL.createObjectURL(compressedFile));
    } catch (error) {
      console.log(error);
    }
  };
  const openai = new OpenAI({
    apiKey: `${api}`,
    dangerouslyAllowBrowser: true,
  });

  async function fetchImageBlob(imageUrl) {
    console.log(imageUrl);
    const response = await fetch(imageUrl);
    const blob = await response.blob();
    return blob;
  }

  const generateImage = async () => {
    // Check required fields
    const { botName, entheogenicSpecialty, tone, focusArea } =
      validation.values;
    if (
      !botName ||
      !tone ||
      !focusArea ||
      !entheogenicSpecialty ||
      (Array.isArray(focusArea) && focusArea.length === 0) ||
      (Array.isArray(entheogenicSpecialty) && entheogenicSpecialty.length === 0)
    ) {
      toast.error(
        "Please fill in all required fields before generating a logo."
      );
      return;
    }

    setLoading((currentLoading) => !currentLoading);
    const response = await openai.images.generate({
      model: "dall-e-2",
      prompt: `Create a digital avatar of a futuristic bot named ${validation.values.botName}, specialized in ${validation.values.entheogenicSpecialty} (e.g., Psilocybin Therapy). Reflect its Focus Areas: ${validation.values.focusArea} (e.g., Mental Health) with a ${validation.values.tone} tone (e.g., calming) using sleek, glowing accents and holographic patterns. Subtly integrate the name via engraved or holographic text. Add psychedelic motifs (e.g., mushrooms, mandalas) to highlight its purpose. Set in a high-tech or nature-infused background (e.g., glowing forest or lab) that matches the tone.`,
      n: 1,
      size: "512x512",
    });
    uploadImage.mutate(response?.data[0].url);
  };
  const uploadImage = useMutation(
    (url) => {
      return http.post("/user/upload-icon", { imageUrl: url });
    },
    {
      onSuccess: ({ data }) => {
        validation.setFieldTouched("iconUrl", true);
        validation.setFieldValue("iconUrl", data.imageUrl);
        setPreviewAvatar(data.imageUrl);
        setLoading((currentLoading) => !currentLoading);
      },
      onError: (error) => {
        switch (error.response.status) {
          case 500: {
            toast.error("One problem is on server. Please try again later.");
            break;
          }
          case 404: {
            toast.error("Please check network status. And then try again.");
            break;
          }
          default:
            toast.error(error.message);
        }
      },
    }
  );

  const handlePractitionerChange = (selectedOptions) => {
    const selectedValues = selectedOptions.map((option) => option.value);
    validation.setFieldValue("practitionerTitle", selectedValues);
  };
  const handleEntheogenicChange = (selectedOptions) => {
    const selectedValues = selectedOptions.map((option) => option.value);
    validation.setFieldValue("entheogenicSpecialty", selectedValues);
  };

  return (
    <>
      <div className="flex-flex-col">
        <div className="w-full user-chat mt-sm-0 ">
          {error && error ? (
            <Alert
              color="red"
              style={{ marginTop: "13px" }}
              isOpen={visible}
              dismissible
              className="flex justify-between items-center"
            >
              {error}
              <Button
                color="inherit"
                onClick={onDismiss}
                className="cursor-pointer"
              >
                <XMarkIcon width={"20px"} />
              </Button>
            </Alert>
          ) : null}
          {success ? (
            <Alert
              color="green"
              style={{ marginTop: "13px" }}
              isOpen={visible}
              dismissible
              className="flex justify-between items-center"
            >
              {success}
              <Button
                color="inherit"
                onClick={onDismiss}
                className="cursor-pointer"
              >
                <XMarkIcon width={"20px"} />
              </Button>
            </Alert>
          ) : null}
          <form noValidate onSubmit={validation.handleSubmit}>
            <div className="pb-12">
              <div class="flex w-full items-center justify-center gap-4">
                <span
                  className="h-20 w-20"
                  type="button"
                  id="radix-:r52:"
                  aria-haspopup="menu"
                  aria-expanded="false"
                  data-state="closed"
                  data-testid="undefined-button"
                  // onClick={() => document.getElementById("file-input").click()}
                >
                  <div className="flex relative h-full w-full items-center justify-center rounded-full border-2 border-dashed border-token-border-medium">
                    {previewAvatar ? (
                      <>
                        <img
                          className="h-full w-full rounded-full"
                          width="80"
                          height="80"
                          src={previewAvatar}
                          alt={botDetails?.botName}
                        />
                        {isUploading && (
                          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75">
                            <svg
                              stroke="currentColor"
                              fill="none"
                              strokeWidth="2"
                              viewBox="0 0 24 24"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="text-center h-1/4 w-1/4 animate-spin"
                              height="1em"
                              width="1em"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <line x1="12" y1="2" x2="12" y2="6"></line>
                              <line x1="12" y1="18" x2="12" y2="22"></line>
                              <line
                                x1="4.93"
                                y1="4.93"
                                x2="7.76"
                                y2="7.76"
                              ></line>
                              <line
                                x1="16.24"
                                y1="16.24"
                                x2="19.07"
                                y2="19.07"
                              ></line>
                              <line x1="2" y1="12" x2="6" y2="12"></line>
                              <line x1="18" y1="12" x2="22" y2="12"></line>
                              <line
                                x1="4.93"
                                y1="19.07"
                                x2="7.76"
                                y2="16.24"
                              ></line>
                              <line
                                x1="16.24"
                                y1="7.76"
                                x2="19.07"
                                y2="4.93"
                              ></line>
                            </svg>
                          </div>
                        )}
                      </>
                    ) : (
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="text-4xl"
                      >
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M12 3C12.5523 3 13 3.44772 13 4L13 11H20C20.5523 11 21 11.4477 21 12C21 12.5523 20.5523 13 20 13L13 13L13 20C13 20.5523 12.5523 21 12 21C11.4477 21 11 20.5523 11 20L11 13L4 13C3.44772 13 3 12.5523 3 12C3 11.4477 3.44772 11 4 11L11 11L11 4C11 3.44772 11.4477 3 12 3Z"
                          fill="currentColor"
                        ></path>
                      </svg>
                    )}
                  </div>
                </span>
                <Button className="rounded-md" onClick={generateImage}>
                  {loading ? (
                    <CircularProgress size="20px" color="inherit" />
                  ) : (
                    "Generate Logo"
                  )}
                </Button>{" "}
                {/* <input
                  accept="image/png,.png,image/jpeg,.jpg,.jpeg,image/gif,.gif,image/webp,.webp"
                  multiple=""
                  tabIndex="-1"
                  type="file"
                  className="hidden"
                  id="file-input"
                  onChange={imageSelect}
                /> */}
              </div>
              <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-6">
                <div className="col-span-full">
                  <label className="block text-sm font-medium leading-6 text-gray-900">
                    Assistant’s Name
                  </label>{" "}
                  <div className="mt-1">
                    <input
                      id="botName"
                      name="botName"
                      type="text"
                      className="w-full text-sm overflow-y-auto rounded-lg border border-[#d9d9d9] px-3 py-2 focus:border focus:outline-1   "
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.botName || ""}
                      error={
                        validation.touched.botName &&
                        Boolean(validation.errors.botName)
                      }
                      helperText={
                        validation.touched.botName && validation.errors.botName
                      }
                    />
                  </div>
                </div>
                <div className="col-span-full">
                  <label className="block text-sm font-medium leading-6 text-gray-900">
                    Coach Name
                  </label>
                  <div className="mt-1">
                    <input
                      id="coachName"
                      name="coachName"
                      type="text"
                      className="w-full text-sm overflow-y-auto rounded-lg border border-[#d9d9d9] px-3 py-2 focus:border focus:outline-1   "
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.coachName || ""}
                      error={
                        validation.touched.coachName &&
                        Boolean(validation.errors.coachName)
                      }
                      helperText={
                        validation.touched.coachName &&
                        validation.errors.coachName
                      }
                    />
                  </div>
                </div>
                <div className="col-span-full">
                  <label className="block text-sm font-medium leading-6 text-gray-900">
                    Practitioner Title
                  </label>
                  <div className="mt-1">
                    <CreatableSelect
                      isMulti
                      options={practitionerTitleoptions}
                      value={practitionerTitleoptions.filter((option) =>
                        validation.values.practitionerTitle.includes(
                          option.value
                        )
                      )}
                      onChange={handlePractitionerChange}
                      className="basic-multi-select"
                      classNamePrefix="select"
                      placeholder=""
                    />
                    {validation.touched.practitionerTitle &&
                      validation.errors.practitionerTitle && (
                        <p className="text-red-500 text-sm mt-1">
                          {validation.errors.practitionerTitle}
                        </p>
                      )}
                  </div>
                </div>
                <div className="col-span-full">
                  <label className="block text-sm font-medium leading-6 text-gray-900">
                    Tone
                  </label>
                  <div className="mt-1">
                    <Select
                      id="tone"
                      className="basic-multi-select"
                      classNamePrefix="select"
                      options={toneOptions}
                      value={toneOptions.filter((option) =>
                        validation.values.tone.includes(option.value)
                      )}
                      onChange={(selectedOptions) =>
                        validation.setFieldValue("tone", selectedOptions.value)
                      }
                      onBlur={() => validation.setFieldTouched("tone", true)}
                      placeholder=""
                    />
                    {validation.touched.tone && validation.errors.tone && (
                      <p className="text-red-500 text-sm mt-1">
                        {validation.errors.tone}
                      </p>
                    )}
                  </div>
                </div>
                <div className="col-span-full">
                  <label className="block text-sm font-medium leading-6 text-gray-900">
                    Focus Area
                  </label>
                  <div className="mt-1">
                    <CreatableSelect
                      id="focusArea"
                      name="focusArea"
                      options={focusAreaOptions}
                      isMulti // Allows multiple selections
                      className="basic-multi-select"
                      classNamePrefix="select"
                      placeholder=""
                      value={focusAreaOptions.filter((option) =>
                        validation.values.focusArea?.includes(option.value)
                      )}
                      onChange={(selectedOptions) =>
                        validation.setFieldValue(
                          "focusArea",
                          selectedOptions.map((option) => option.value)
                        )
                      }
                      onBlur={() =>
                        validation.setFieldTouched("focusArea", true)
                      }
                    />
                    {validation.touched.focusArea &&
                      validation.errors.focusArea && (
                        <p className="text-red-500 text-sm mt-1">
                          {validation.errors.focusArea}
                        </p>
                      )}
                  </div>
                </div>
                <div className="col-span-full">
                  <label className="block text-sm font-medium leading-6 text-gray-900">
                    Entheogenic Specialty
                  </label>
                  <div className="mt-1">
                    <CreatableSelect
                      id="entheogenicSpecialty"
                      name="entheogenicSpecialty"
                      options={entheogenicOptions}
                      isMulti
                      className="basic-multi-select"
                      classNamePrefix="select"
                      placeholder=""
                      value={entheogenicOptions.filter((option) =>
                        validation.values.entheogenicSpecialty?.includes(
                          option.value
                        )
                      )}
                      onChange={handleEntheogenicChange}
                      onBlur={() =>
                        validation.setFieldTouched("entheogenicSpecialty", true)
                      }
                    />
                    {validation.touched.entheogenicSpecialty &&
                      validation.errors.entheogenicSpecialty && (
                        <p className="text-red-500 text-sm mt-1">
                          {validation.errors.entheogenicSpecialty}
                        </p>
                      )}
                  </div>
                </div>
                <div className="col-span-full">
                  <label className="block text-sm font-medium leading-6 text-gray-900">
                    Greeting
                  </label>{" "}
                  <div className="mt-1">
                    <textarea
                      name="greeting"
                      placeholder="How can I help you, Seeker?"
                      rows={2}
                      className="w-full text-sm overflow-y-auto rounded-lg border border-[#d9d9d9] px-3 py-2 focus:border focus:outline-1  "
                      disabled
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.greeting || ""}
                      error={
                        validation.touched.greeting &&
                        Boolean(validation.errors.greeting)
                      }
                      helperText={
                        validation.touched.greeting &&
                        validation.errors.greeting
                      }
                    />
                  </div>
                </div>
                <div className="col-span-full">
                  <label className="block text-sm font-medium leading-6 text-gray-900">
                    Conversation starters
                  </label>{" "}
                  <BotStarters />
                </div>
                <div className="col-span-full">
                  <label className="block text-sm font-medium leading-6 text-gray-900">
                    Documents
                  </label>
                  <BotAllDocs />
                </div>
                <div className="col-span-full">
                  <label className="block text-sm font-medium leading-6 text-gray-900">
                    Websites
                  </label>
                  <BotTrainWebsites />
                </div>
              </div>
              <div className="col-span-full">
                <div className="flex mt-3 w-full justify-end items-center">
                  <Button type="submit">Save</Button>{" "}
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default BotUpdates;
